import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { TopNav } from '../components/top-nav.js';
import type { Brand, BrandMemoListEntry } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { formatDate, formatRelativeTimeCompact, relativeDayWord } from '../lib/format.js';
import { brandMemoListQuery, brandsQuery, meQuery } from '../lib/queries.js';

/**
 * Memo archive — `/brand/$slug/memos`. Lists every weekly memo mag
 * has shipped for this brand, newest first. Click into one to read
 * the full memo at `/brand/$slug/memos/$memoId`.
 *
 * The pinned card on /home shows the LATEST memo only; this surface
 * is for re-reading past weeks + the trust-building "look at mag's
 * track record" moment.
 */
export const Route = createFileRoute('/brand/$slug/memos/')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    await queryClient.ensureQueryData(brandMemoListQuery(brand.id));
    return { brand };
  },
  component: MemoArchivePage,
});

function MemoArchivePage() {
  const { brand } = Route.useRouteContext();
  useBrandTitle(brand.slug, ['memos']);
  const { data: memos, isLoading } = useQuery(brandMemoListQuery(brand.id));

  return (
    <div className="min-h-dvh bg-[var(--paper)]">
      <TopNav active="home" />
      <main className="mx-auto max-w-[760px] px-4 py-8 sm:px-6 sm:py-10">
        <Link
          to="/brand/$slug/home"
          params={{ slug: brand.slug }}
          className="mb-4 inline-block font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
        >
          {'←'} <Trans>back to home</Trans>
        </Link>
        <header className="mb-6">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            <Trans>↘ {brand.name.toLowerCase()} · memos</Trans>
          </p>
          <h1 className="mt-1 font-display text-[28px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
            <Trans>every memo, every week.</Trans>
          </h1>
          <p className="mt-2 text-[13px] text-[var(--ink)]/65">
            <Trans>the running letter. monday memos i've sent — clickable to re-read.</Trans>
          </p>
        </header>

        {isLoading ? (
          <Loading />
        ) : !memos || memos.length === 0 ? (
          <Empty brand={brand} />
        ) : (
          <ul className="flex flex-col gap-3">
            {memos.map((memo) => (
              <MemoRow key={memo.id} memo={memo} brand={brand} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function Loading() {
  return (
    <p className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 text-center font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/45 shadow-[4px_4px_0_var(--spell)]">
      <Trans>reading the archive…</Trans>
    </p>
  );
}

function Empty({ brand }: { brand: Brand }) {
  return (
    <div className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--spell)]">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>nothing yet</Trans>
      </p>
      <h3 className="mt-1 font-display text-[16px] font-bold tracking-[-0.01em] text-[var(--ink)]">
        <Trans>first memo lands monday.</Trans>
      </h3>
      <p className="mt-2 text-[13px] text-[var(--ink)]/65">
        <Trans>
          memos publish weekly at monday 7am in {brand.name}'s tz. once they start landing they
          archive here.
        </Trans>
      </p>
    </div>
  );
}

function MemoRow({ memo, brand }: { memo: BrandMemoListEntry; brand: Brand }) {
  const periodLabel = formatPeriodLabel(memo.periodStart);
  const generatedLabel = formatGeneratedAt(memo.generatedAt);
  return (
    <li>
      <Link
        to="/brand/$slug/memos/$memoId"
        params={{ slug: brand.slug, memoId: memo.id }}
        className="block rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] transition-all hover:shadow-[5px_5px_0_var(--spell)] sm:p-6"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            ↘ {periodLabel}
          </p>
          <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            <Trans>shipped {generatedLabel}</Trans>
          </span>
        </div>
        <h2 className="mt-2 font-display text-[18px] font-bold leading-snug tracking-[-0.01em] text-[var(--ink)]">
          {memo.headline}
        </h2>
      </Link>
    </li>
  );
}

function formatPeriodLabel(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    const fmt = formatDate(d, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
    return i18n._(msg`week of ${fmt.toLowerCase()}`);
  } catch {
    return i18n._(msg`week of ${iso}`);
  }
}

function formatGeneratedAt(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return iso;
  const days = Math.round(ms / (24 * 60 * 60 * 1000));
  // The BUCKETING stays ours; the WORDS stop being. relativeDayWord gives
  // "today"/"yesterday" ("hoy"/"ayer") and formatRelativeTimeCompact the
  // narrow "3d ago" forms — both from Intl, both already built in wave 2.
  // This function was a hand-rolled duplicate of them in English.
  if (days < 1) return relativeDayWord(0);
  if (days === 1) return relativeDayWord(-1);
  return formatRelativeTimeCompact(iso);
}
